import { combineReducers } from 'redux';
import { createReducer } from 'typesafe-actions';

import { IAccessControlConfigState } from '@/features/roles/store/models/accessControl.model';
import { AccessControlActionsValues, TAccessControlActions } from '@/features/roles/store/types/accessControl.types';
import { EScope } from '@/features/roles/store/models/role.model';
import { featuresNames, IFeature, TFeatureNames } from '@/features/roles/store/models/features.model';
import { IAdvancedRole, roleSlugValues } from '@/features/roles/store/models/advancedRole.model';
import { AdvancedRoleReducer } from '@/features/roles/store/reducers/advancedRole.reducer';

export function getRoleFeatures(role: IAdvancedRole, allRoles: IAdvancedRole<IFeature>[]): IFeature[] {
  return (!!role && !!allRoles && allRoles.find((configRole) => configRole.slug === role.slug)?.features) || [];
}

const initialState: IAccessControlConfigState = {
  value: [
    {
      slug: 'CONFIG',
      name: 'Config',
      description: 'Load dashboard config',
      parent: 'SUPPORT_USER_READ_ONLY',
      scopes: [EScope.READ_ONLY, EScope.READ_ALL],
      features: [
        {
          name: 'Config',
          slug: 'CONFIG',
          endpoints: [
            {
              method: 'GET',
              url: 'config.json',
            },
            {
              method: 'GET',
              url: 'core-connect/dashboard/config',
            },
          ],
          routes: ['/*'],
        },
        {
          name: 'Auth',
          slug: 'AUTH',
          endpoints: [
            {
              method: 'POST',
              url: 'oauth/token',
            },
            {
              method: 'GET',
              url: 'core-connect/support-user/+',
            },
          ],
          routes: ['/auth/*'],
        },
      ],
    },
  ],
  error: undefined,
  isLoading: false,
};

function addFeatures(features: TFeatureNames[], source: IFeature[], target: IFeature[]): IFeature[] {
  return [
    ...source.filter((feature) => !features.includes(feature.name)),
    ...target.filter((feature) => features.includes(feature.name)),
  ];
}

export const AccessControlConfigReducer = createReducer<IAccessControlConfigState, TAccessControlActions>(initialState)
  .handleType(AccessControlActionsValues.GET_ACCONFIG_SUCCESS, (state, action) => {
    let config = action.config.map((role) => ({
      ...role,
      features: addFeatures([featuresNames.AUTH, featuresNames.CONFIG], role.features, action.features),
    }));

    const isSetPinActivated = action.option.actions.includes('setPin');

    if (config) {
      config = config.map((role) => ({
        ...role,
        features: addFeatures(
          [
            ...[...(action.isCrypto ? [featuresNames.KEYS] : ([] as TFeatureNames[]))],
            ...[
              ...(role.slug !== roleSlugValues.READ_ONLY && isSetPinActivated
                ? [featuresNames.SET_PIN, featuresNames.EDIT_CARDS]
                : ([] as TFeatureNames[])),
            ],
          ],
          role.features,
          action.features,
        ),
        scopes: [...role.scopes, ...(action.isCrypto ? [EScope.KEYS] : [])],
      }));
    }

    return {
      ...state,
      value: [...initialState.value, ...config],
      error: undefined,
      isLoading: false,
    };
  })
  .handleType(AccessControlActionsValues.GET_ACCONFIG_FAILED, (state, action) => ({
    ...state,
    value: undefined,
    error: action.error,
    isLoading: false,
  }));

export const AccessControlReducer = combineReducers({ role: AdvancedRoleReducer, config: AccessControlConfigReducer });
